import '../../OrganizationTables.scss';
import {
  Button,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@trackman/web-shared-components';

import { useTranslation } from 'react-i18next';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { ReactComponent as SendIcon } from 'images/icons/Send.svg';
import { useState } from 'react';
import { Invite } from 'types/organization/invites';
import DeleteInvitationsModal from '../DeleteInvitationsModal/DeleteInvitationsModal';
import { handleSearchStudent } from 'utilities/search-helper';
import { useResendInvites } from 'queryHooks/Organization/Invites/useResendInvites';
import { useOrganizationStore } from 'stores/Organization/OrganizationStore';
import { useStores } from 'index';
import { OrganizationTableInitials } from '../../OrganizationTableInitials/OrganizationTableInitials';
import { NoEntriesMatching } from '../../../../../components/NoEntriesMatching/NoEntriesMatching';

type Props = {
  inviteList: Invite[];
  filter: string;
};

type PendingInvitesTableEntry = {
  id: string;
  name: string | null;
  classNames: string | null;
  email: string;
};

export const PendingInviteList = ({ inviteList, filter }: Props) => {
  const { t } = useTranslation();
  const notificationsStore = useStores().notificationStore;

  const [selectedInviteIds, setSelectedInviteIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [isDeleteInvitesModalOn, setIsDeleteInvitesModalOn] = useState<boolean>(false);
  const [inviteIdUnderAction, setInviteIdUnderAction] = useState<string>('');
  const selectedOrganizationId = useOrganizationStore((state) => state.selectedOrganizationId);

  const resendInvites = useResendInvites(selectedOrganizationId);

  const allTableEntries: PendingInvitesTableEntry[] = inviteList.map((invite) => ({
    id: invite.id,
    name: invite.name,
    email: invite.email,
    classNames: invite?.classes?.map((c) => c.name).join(', ') ?? null,
  }));

  const filteredTableEntries = handleSearchStudent(filter, allTableEntries);

  const pageTableEntries = filteredTableEntries.slice(page * entriesPerPage, page * entriesPerPage + entriesPerPage);

  const handleSelectAll = () => {
    if (selectedInviteIds.length === inviteList.length) {
      setSelectedInviteIds([]);
    } else {
      setSelectedInviteIds(inviteList.map((student) => student.id));
    }
  };

  const handleSelectEntry = (id: string) => {
    if (selectedInviteIds.includes(id)) {
      setSelectedInviteIds(selectedInviteIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedInviteIds([...selectedInviteIds, id]);
    }
  };

  const handleResendSingleInvitation = (id: string) => {
    resendInvites.mutate([id], {
      onSuccess: ({ succeeded, failed, total }) => handleResendInvitesResponse(succeeded, failed, total),
    });
  };

  const handleResendMultipleInvitation = () => {
    resendInvites.mutate(selectedInviteIds, {
      onSuccess: ({ succeeded, failed, total }) => handleResendInvitesResponse(succeeded, failed, total),
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setEntriesPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenRemoveInviteDialog = (id: string) => {
    setInviteIdUnderAction(id);
    setIsDeleteInvitesModalOn(true);
  };

  const handleDeleteInvites = () => {
    setIsDeleteInvitesModalOn(false);
    if (inviteIdUnderAction) {
      setInviteIdUnderAction('');
    } else {
      setSelectedInviteIds([]);
    }
  };

  const handleResendInvitesResponse = (succeeded: number, failed: number, total: number) => {
    if (total === 1) {
      if (failed === 0) {
        notificationsStore.addToast('success', t('InvitationResentSuccessfully', { ns: 'studentsPage' }), 'success');
      } else if (succeeded === 0) {
        notificationsStore.addToast('error', t('InvitationResentError', { ns: 'studentsPage' }), 'error');
      }
    } else {
      if (failed === 0) {
        notificationsStore.addToast('success', t('InvitationsResentSuccessfully', { ns: 'studentsPage' }), 'success');
      } else if (succeeded === 0) {
        notificationsStore.addToast('error', t('InvitationsResentError', { ns: 'studentsPage' }), 'error');
      } else {
        notificationsStore.addToast('warning', t('InvitationsResentPartially', { ns: 'studentsPage', succeeded }), 'info');
      }
    }
  };

  return (
    <div className='organization-table-card-wrapper invite-list'>
      <div className='organization-table-header'>
        <strong>{t('PendingInvites', { ns: 'studentsPage', number: filteredTableEntries.length })}</strong>
        <div className='organization-table-buttons'>
          <Button
            startIcon={<SendIcon />}
            label={t('ResendInvitations', { ns: 'studentsPage' })}
            className='organization-button'
            onClick={() => handleResendMultipleInvitation()}
            intent='secondary'
            disabled={selectedInviteIds.length === 0}
          />
          <Button
            startIcon={<TrashIcon />}
            label={t('DeleteInvitations', { ns: 'studentsPage' })}
            className='organization-button'
            onClick={() => setIsDeleteInvitesModalOn(true)}
            intent='secondary'
            disabled={selectedInviteIds.length === 0}
          />
        </div>
      </div>
      {filteredTableEntries.length > 0 ? (
        <Table sx={{ minWidth: 750 }} aria-labelledby='studentsTable' className='tmu-table'>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }} padding='checkbox'>
                <Checkbox
                  color='primary'
                  indeterminate={selectedInviteIds.length > 0 && selectedInviteIds.length < allTableEntries.length}
                  checked={allTableEntries.length > 0 && selectedInviteIds.length === allTableEntries.length}
                  onChange={() => handleSelectAll()}
                  label={undefined}
                />
              </TableCell>
              <TableCell sx={{ width: 50 }}></TableCell>
              <TableCell sx={{ width: 250 }}>{t('Name', { ns: 'studentsPage' })}</TableCell>
              <TableCell sx={{ width: 250 }}>{t('Classes', { ns: 'studentsPage' })}</TableCell>
              <TableCell sx={{ width: 250 }}>{t('Email', { ns: 'studentsPage' })}</TableCell>
              <TableCell sx={{ width: 50 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageTableEntries.map((entry) => (
              <TableEntry
                key={entry.id}
                isItemSelected={selectedInviteIds.includes(entry.id)}
                tableEntry={entry}
                handleSelectEntry={() => handleSelectEntry(entry.id)}
                openRemoveEntryDialog={() => handleOpenRemoveInviteDialog(entry.id)}
                handleResendInvite={() => handleResendSingleInvitation(entry.id)}
              />
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={filteredTableEntries.length}
                rowsPerPage={entriesPerPage}
                page={page}
                onPageChange={(e, newPage: number) => setPage(newPage)}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        </Table>
      ) : (
        <NoEntriesMatching />
      )}
      {isDeleteInvitesModalOn && (
        <DeleteInvitationsModal
          isOpen={isDeleteInvitesModalOn}
          inviteIds={inviteIdUnderAction ? [inviteIdUnderAction] : selectedInviteIds}
          close={handleDeleteInvites}
        />
      )}
    </div>
  );
};

const TableEntry = ({ isItemSelected, tableEntry, handleSelectEntry, openRemoveEntryDialog, handleResendInvite }) => {
  const handleResend = (event) => {
    event.stopPropagation();
    handleResendInvite();
  };
  const selectEntry = (event) => {
    event.stopPropagation();
    handleSelectEntry();
  };

  const handleRemoveEntry = (event) => {
    event.stopPropagation();
    openRemoveEntryDialog();
  };
  return (
    <TableRow
      hover
      onClick={(event) => selectEntry(event)}
      role='checkbox'
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={tableEntry.id}
      selected={isItemSelected}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell padding='checkbox'>
        <Checkbox color='primary' checked={isItemSelected} label={undefined} />
      </TableCell>
      <TableCell className='initials-cell'>
        <OrganizationTableInitials name={tableEntry.name} />
      </TableCell>
      <TableCell>{tableEntry.name}</TableCell>
      <TableCell>{tableEntry.classNames}</TableCell>
      <TableCell>{tableEntry.email}</TableCell>
      <TableCell>
        <div className='buttons-cell-content'>
          <IconButton icon={() => <SendIcon />} onClick={handleResend} />
          <IconButton icon={() => <TrashIcon />} onClick={handleRemoveEntry} />
        </div>
      </TableCell>
    </TableRow>
  );
};
